// ─────────────────────────────────────────────────────────────────────────────
// firebase/collections.ts – Ablage-Vertrag der Auftraege (REQ-DM-015/015a)
//
// EINZIGE Ablage der Auftraege ist die TOP-LEVEL Collection `printJobs`
// (REQ-DM-015a). Alle Beteiligten verwenden dieselbe Collection:
//   - Erzeuger:  `payfixwebhook`            -> db.collection('printJobs')
//   - Zusteller: Druckserver-Daemon         -> print-server/src/query.js
//   - Leser:     Dashboard, Bildschirm-Kanaele (useScreenChannel.ts)
//
// Die frueher vom Dashboard verwendete Subcollection unterhalb von
// `companies/{companyId}` hatte KEINEN Erzeuger: dort abgelegte Auftraege
// erreichten nie ein Geraet (Befund OP-40, Nachdruck real defekt). Ihre
// abweichende Schreibweise ist bewusst nirgends mehr ausgeschrieben, damit der
// Waechter in test/printJobsCollection.test.ts scharf bleiben kann.
// Die Mandantentrennung laeuft in der top-level Collection ueber das Feld
// `companyId` als Abfragefilter (siehe CLAUDE.md: Scoping im App-Code).
//
// Bewusst frei von Seiteneffekten (kein Import von `@/firebase/config`, keine
// Firebase-Initialisierung), damit der Vertragstest dieses Modul ohne
// Firebase-Umgebung laden kann.
// ─────────────────────────────────────────────────────────────────────────────

import type { PrintJob } from './models';
import { PrintJobStatus } from './models';

/**
 * Name der kanonischen, top-level Auftrags-Collection (REQ-DM-015a).
 * Wird von JEDEM Lese- und Schreibpfad des Dashboards verwendet; nirgends darf
 * der Collection-Name erneut als Zeichenkette ausgeschrieben werden.
 */
export const PRINT_JOBS_COLLECTION = 'printJobs';

/**
 * Feldnamen, ueber die der Druckserver-Daemon seine Zustell-Abfrage bildet
 * (REQ-LC-010 + REQ-LC-013, `print-server/src/query.js`):
 *   printJobs WHERE printserverId == … AND status == 'pending'
 *             AND holdUntilReady == false ORDER BY createdAt
 *
 * Ein vom Dashboard erzeugter Auftrag (Nachdruck) muss diese Felder tragen,
 * sonst nimmt ihn kein Geraet auf. Der Vertragstest prueft beide Seiten
 * gegeneinander.
 *
 * `holdUntilReady` ist mit T-M15.3 hinzugekommen (vierte Filterbedingung,
 * REQ-LC-013). Es steht hier als FELDNAME, nicht als Wert — den Wert eines im
 * Dashboard angelegten Auftrags fuehrt NEW_JOB_HOLD_UNTIL_READY (immer `false`).
 */
export const DAEMON_QUERY_FIELDS = {
  printserverId:  'printserverId',
  status:         'status',
  holdUntilReady: 'holdUntilReady',
  createdAt:      'createdAt',
} as const;

/** Zustellstatus, mit dem ein neuer Auftrag angelegt wird (REQ-LC-004). */
export const NEW_JOB_STATUS = PrintJobStatus.PENDING;

/**
 * DAS HALTE-KENNZEICHEN EINES IM DASHBOARD ANGELEGTEN AUFTRAGS
 * (REQ-LC-009 Praezisierung 1 (b) und (c)) — immer `false`, ohne Bedingung.
 *
 * Ein `printJobs`-Dokument entsteht an DREI Stellen (REQ-SC-061 Pruefzeile,
 * Punkt (b)): am Auftragseingang (`ingestPrintJob`, beide Eingaenge), beim
 * NACHDRUCK (`buildResendJobData`) und bei der Anlage aus der Verwaltung
 * (`createPrintJob`). Nur die erste bildet den Wert nach den vier Bedingungen
 * aus REQ-LC-009; die beiden Stellen des Dashboards halten NIE zurueck:
 *
 *   Ein Nachdruck ist die ausdrueckliche Anweisung eines Menschen, JETZT zu
 *   drucken. Er hat keinen Produzenten der Freigabe — niemand klickt am
 *   Kitchen TV „Fertig" fuer einen Nachdruck —, und gehalten wuerde er nie
 *   gedruckt. Dieselbe Sackgasse wie beim Kassenbon (REQ-LC-009 Abgrenzung 1).
 *
 * WARUM DAS FELD UEBERHAUPT MITGESCHRIEBEN WIRD, statt es wegzulassen: der
 * Zustell-Listener filtert auf `holdUntilReady == false` (REQ-LC-013), und
 * Firestore kann nicht auf „Feld fehlt" abfragen. Ein Nachdruck ohne das Feld
 * traegt `pending` und `printserverId`, ist fuer den Daemon aber UNSICHTBAR:
 * die Oberflaeche meldet Erfolg, es druckt nichts, nach vier Stunden verfaellt
 * der Auftrag — woertlich die Fehlerklasse von OP-40 auf einem neuen Weg. Die
 * einmalige Nachziehung des Bestands (T-M15.9) deckt das NICHT, weil hier bei
 * jedem Nachdruck ein NEUES Dokument entsteht.
 *
 * Bewusst EIN Wert fuer beide Dashboard-Anlagestellen: `createPrintJob`
 * (services/printJobService.ts) fuehrt ihn von hier, wie schon `NEW_JOB_STATUS`.
 */
export const NEW_JOB_HOLD_UNTIL_READY = false;

/**
 * Ergebnis der Vorbereitung eines Nachdrucks. Bewusst ein Ergebnis-Objekt statt
 * einer Ausnahme: die Oberflaeche muss den Fall „nicht zustellbar" ANZEIGEN und
 * darf ihn nicht als Erfolg melden — genau das war der Defekt aus OP-40.
 */
export type ResendPreparation =
  | { ok: true;  data: Record<string, unknown> }
  | { ok: false; reason: 'missingPrintserver' };

/**
 * Fuer den Nachdruck benoetigte Felder des Ursprungsauftrags.
 *
 * ⚠ `printserverId` GEHOERT SEIT DEM 2026-09-28 NICHT MEHR DAZU (T-M45.6,
 * REQ-DM-074 (7)). Bis dahin stand es hier unter den optionalen Feldern, und
 * `buildResendJobData` nahm es VOR dem Wert des Druckers — ein Nachdruck nach
 * dem Umhaengen des Druckservers ging an den alten, fremden Pi (Befund
 * 2409_2). Ein Aufrufer darf einen ganzen `PrintJob` uebergeben; das Feld wird
 * nicht gelesen. Berichtigt statt geloescht (REQ-OPS-036 Praez. 6).
 */
export type ResendSourceJob = Pick<
  PrintJob,
  'companyId' | 'printerId' | 'registerId' | 'payload'
> & Partial<Pick<PrintJob, 'locationId' | 'receiptNumber' | 'source'>>;

export interface ResendOptions {
  /**
   * Druckserver des Zielkanals — ALLEIN aus dem `printers`-Dokument
   * (REQ-DM-074 (7), „in jedem Fall nimmt der Nachdruck den Druckserver des
   * Druckers"). Ermittelt von `utils/nachdruckDruckserver.ts` (geladene Liste,
   * sonst gezieltes Lesen). **Pflicht**, damit kein Aufrufer ihn vergessen
   * kann; `undefined` heisst „der Drucker hat keinen" und fuehrt zu
   * `missingPrintserver`.
   *
   * *Bis zum 2026-09-28: „Vorrang hat die denormalisierte Angabe am
   * Ursprungsauftrag; fehlt sie (Altbestand), reicht die Oberflaeche die
   * Zuordnung aus dem `printers`-Dokument nach." — berichtigt statt geloescht
   * (REQ-OPS-036 Praez. 6).*
   */
  printserverId: string | undefined;
  /** Server-Zeitstempel (`serverTimestamp()`), von der Aufrufseite gereicht. */
  timestamp: unknown;
}

/**
 * Baut die Nutzdaten eines Nachdrucks (REQ-DM-030).
 *
 * Bewusst NICHT uebernommen:
 *   - `jobUuid`  – die Idempotenz-Kennung gehoert zum Ursprungsauftrag; ein
 *                  Nachdruck ist ein neuer Auftrag und darf mit ihm nicht
 *                  verwechselt oder wegdedupliziert werden (REQ-DM-032).
 *   - `workStatus`, `readyAt`, `claimedBy`, `claimedAt`, `printedAt` –
 *                  Bearbeitungs- und Uebernahmezustand des Ursprungsauftrags.
 *   - `releasedAt` – der Zeitpunkt einer Freigabe entsteht erst mit ihr
 *                  (REQ-LC-014); ein Nachdruck wird nie gehalten und hat
 *                  deshalb nichts freizugeben.
 *
 * Ohne aufloesbaren Druckserver entsteht KEIN Dokument: ein `pending`-Auftrag
 * ohne `printserverId` faellt durch die Daemon-Abfrage und bliebe stumm liegen
 * (Bildschirm-Kanaele haben systembedingt keinen Druckserver, REQ-SC-021).
 */
export function buildResendJobData(
  job: ResendSourceJob,
  opts: ResendOptions,
): ResendPreparation {
  // ALLEIN der Drucker (REQ-DM-074 (7)) — kein Rueckfall auf den
  // Ursprungsauftrag, auch dann nicht, wenn der Drucker keinen hat.
  // *Bis zum 2026-10-05 stand hier, die Form ohne Klammer nach dem `=` sei
  // wegen `kitchenGuards.test.ts` noetig; seit T-M47.5 (OP-260) erkennt der
  // Waechter Funktionsgrenzen selbst, die Schreibform ist frei.*
  const printserverId = opts.printserverId?.trim() ?? '';
  if (!printserverId) return { ok: false, reason: 'missingPrintserver' };

  const data: Record<string, unknown> = {
    companyId:     job.companyId,
    printerId:     job.printerId,
    printserverId: printserverId,
    registerId:    job.registerId,
    payload:       job.payload,
    source:        job.source ?? 'dashboard',
    status:        NEW_JOB_STATUS,
    // Ohne Bedingung `false` (REQ-LC-009 Praez. 1 (b)) — siehe die Begruendung
    // an NEW_JOB_HOLD_UNTIL_READY. Fehlte es, waere der Nachdruck fuer den
    // Zustell-Listener unsichtbar und verfiele stumm.
    holdUntilReady: NEW_JOB_HOLD_UNTIL_READY,
    createdAt:     opts.timestamp,
    updatedAt:     opts.timestamp,
  };

  if (job.locationId)    data['locationId']    = job.locationId;
  if (job.receiptNumber) data['receiptNumber'] = job.receiptNumber;

  return { ok: true, data };
}
